import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  FileText,
  Bell,
  CheckSquare,
  User,
  Network,
  Database,
  UsersRound,
  Clock,
  Wallet,
  CalendarDays,
  Calculator,
  Receipt,
  AlertTriangle,
  ClipboardCheck,
  BookmarkCheck,
  FileBarChart,
  Banknote,
  ArrowLeftRight,
  TreePalm,
  ShieldCheck,
  UserCheck,
  FileSearch,
  TrendingUp,
  Wrench,
  FileSignature,
  BookOpen,
  Package,
  Gauge,
  Home,
  Calendar,
  IndianRupee,
  HelpCircle,
  Inbox,
  FileBarChart2,
  BarChart3,
  Landmark,
  PieChart,
  CreditCard,
  Layers,
  Plane,
  Sparkles,
  ClipboardList,
  MapPin,
  Coins,
  ScanFace,
  History,
  ListChecks,
  LifeBuoy,
  Trash2,
  FileCheck,
  ChevronRight,
  Megaphone,
  X,
} from 'lucide-react';
import { canViewPath } from '../constants/permissions';
import { ESS_NAVIGATION } from '../constants/essNavigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SidebarItem = ({ to, label, icon: Icon, active, onClick, isChild, isOpen, unreadBadge }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`sidebar-${label.toLowerCase().replace(/[& ]/g, '-')}`}
      className={`group w-full flex items-center justify-between h-10 px-3 transition-all duration-150 rounded-md select-none shrink-0 gap-3
        ${isChild ? 'pl-8 text-xs' : 'text-sm font-medium'}
        ${
          active
            ? 'bg-blue-50/90 text-[#0052FF] font-semibold border-l-4 border-[#0052FF] shadow-2xs'
            : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 border-l-4 border-transparent'
        }
        md:px-0 md:justify-center lg:px-3 lg:justify-between`}
    >
      <div className="flex items-center gap-2.5 min-w-0 md:w-full md:justify-center lg:justify-start">
        <div className="flex items-center justify-center shrink-0 w-5 h-5 md:mx-auto lg:mx-0">
          <Icon
            className={`w-4.5 h-4.5 transition-colors ${
              active ? 'text-[#0052FF]' : 'text-slate-400 group-hover:text-slate-600'
            }`}
            strokeWidth={active ? 2 : 1.7}
          />
        </div>
        <span
          className={`truncate md:hidden lg:inline whitespace-nowrap ${isOpen ? 'inline' : ''}`}
        >
          {label}
        </span>
      </div>

      {/* Real unread badge or count indicator */}
      {unreadBadge > 0 && (
        <span 
          data-testid="sidebar-notif-badge"
          className="md:hidden lg:inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-[#0052FF] text-white shadow-2xs"
        >
          {unreadBadge > 99 ? '99+' : unreadBadge}
        </span>
      )}
    </Link>
  );
};

const SidebarGroup = ({ label, icon: Icon, parentActive, isExpanded, onExpandClick, children, isOpen }) => {
  return (
    <div className="w-full shrink-0">
      <button
        data-testid={`sidebar-${label.toLowerCase().replace(/[& ]/g, '-')}`}
        onClick={onExpandClick}
        className={`group w-full flex items-center justify-between h-10 px-3 transition-all duration-150 rounded-md select-none text-sm font-medium
          ${
            parentActive
              ? 'bg-blue-50/80 text-blue-900 font-semibold border-l-4 border-blue-600'
              : 'text-slate-700 hover:bg-slate-100/70 hover:text-slate-900 border-l-4 border-transparent'
          }
          md:px-0 md:justify-center lg:px-3 lg:justify-between`}
      >
        <span className="flex items-center gap-2.5 min-w-0 md:w-full md:justify-center lg:justify-start">
          <div className="flex items-center justify-center shrink-0 w-5 h-5 md:mx-auto lg:mx-0">
            <Icon
              className={`w-4.5 h-4.5 transition-colors ${
                parentActive ? 'text-[#0052FF]' : 'text-slate-400 group-hover:text-slate-600'
              }`}
              strokeWidth={parentActive ? 2 : 1.7}
            />
          </div>
          <span
            className={`truncate md:hidden lg:inline whitespace-nowrap ${isOpen ? 'inline' : ''}`}
          >
            {label}
          </span>
        </span>
        <ChevronRight
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 md:hidden lg:block shrink-0 ${
            isExpanded ? 'rotate-90 text-slate-600' : ''
          } ${isOpen ? 'block' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className="w-full flex flex-col mt-0.5 space-y-0.5 bg-slate-50/60 border-l border-slate-200/80 ml-3.5 pl-1 py-1 rounded-r-md">
          {children}
        </div>
      )}
    </div>
  );
};

const SidebarSection = ({ label, isOpen }) => {
  return (
    <div
      className={`px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 md:hidden lg:block shrink-0 ${
        isOpen ? 'block' : ''
      }`}
    >
      {label.replace(/^── /, '')}
    </div>
  );
};

const SidebarFooter = ({ user, isOpen }) => {
  const name = user?.name || user?.email || 'User';
  const empCode = user?.employee_code || user?.emp_code || user?.code || '';
  const rawRole = (user?.role || 'employee').replace(/_/g, ' ');
  const formattedRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
  const companyName = user?.company_name || user?.company?.name || user?.tenant_name || 'HECS Ltd';

  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'EP';

  return (
    <div className="p-3 border-t border-slate-200/80 bg-slate-50/60 flex flex-col md:items-center lg:items-start shrink-0 w-full">
      <div className="hidden md:flex lg:hidden justify-center py-1 w-full">
        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs shadow-2xs">
          {initials}
        </div>
      </div>
      <div className={`w-full md:hidden lg:block ${isOpen ? 'block' : ''}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-xs shadow-2xs shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-xs text-slate-900 truncate" title={name}>
              {name}
            </p>
            <p className="text-[11px] text-slate-500 truncate font-medium">
              {empCode ? `${empCode} · ` : ''}{formattedRole}
            </p>
            <div className="flex items-center gap-1 text-[10px] text-slate-600 font-semibold mt-0.5 truncate">
              <Building2 className="w-3 h-3 text-blue-600 shrink-0" />
              <span className="truncate">{companyName}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isReportingManager, activeView } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState(() => sessionStorage.getItem('sidebar-expanded') || null);
  const [unreadCount, setUnreadCount] = useState(0);
  const navRef = useRef(null);

  // Unread notification count for badge
  useEffect(() => {
    let alive = true;
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/notifications/unread-count`, { withCredentials: true });
        if (alive) setUnreadCount(data?.total || 0);
      } catch (_) {}
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  // Restore sidebar scroll position across navigation
  useLayoutEffect(() => {
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (navRef.current && saved) {
      navRef.current.scrollTop = parseInt(saved, 10) || 0;
    }
    const nav = navRef.current;
    if (!nav) return;
    const onScroll = () => {
      sessionStorage.setItem('sidebar-scroll', String(nav.scrollTop));
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => nav.removeEventListener('scroll', onScroll);
  }, []);

  const handleExpand = (label) => {
    const next = expandedMenu === label ? null : label;
    setExpandedMenu(next);
    if (next) sessionStorage.setItem('sidebar-expanded', next);
    else sessionStorage.removeItem('sidebar-expanded');
  };

  const isSuperAdmin = user?.role === 'super_admin';

  // Super Admin platform menu
  const platformMenu = [
    { label: 'Platform Console', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Tenants', icon: Building2, path: '/tenants' },
    { label: 'Module Control', icon: Shield, path: '/module-control' },
    { label: 'Audit Logs', icon: FileText, path: '/audit-logs' },
    { label: 'Broadcasts', icon: Megaphone, path: '/company-broadcasts' },
    { label: 'Notifications', icon: Bell, path: '/notifications' },
    { label: 'Profile', icon: User, path: '/profile' },
  ];

  // Tenant/business menu
  const tenantMenu = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
    { label: 'My Portal', icon: Home, path: '/ess',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'],
      submenu: ESS_NAVIGATION },
    { label: 'Outstation Conveyance', icon: Plane, path: '/travel',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer', 'department_manager', 'employee'],
      submenu: [
        { label: 'Dashboard', path: '/travel/dashboard', icon: LayoutDashboard },
        { label: 'New Travel Request', path: '/travel/request/new', icon: FileText },
        { label: 'Settlement', path: '/travel/settlement', icon: Receipt },
        { label: 'Approval Inbox', path: '/travel/approval', icon: CheckSquare },
        { label: 'My Assigned Applications', path: '/travel/assigned-queue', icon: BookmarkCheck },
        { label: 'Booking Desk', path: '/travel/booking', icon: Receipt },
        { label: 'Booking Controller', path: '/travel/booking-controller', icon: UserCheck },
        { label: 'Cab Booking', path: '/travel/cab-booking', icon: MapPin },
        { label: 'Hotel Booking', path: '/travel/hotel-booking', icon: MapPin },
        { label: 'Travel Policy Master', path: '/travel/policy', icon: BookOpen },
        { label: 'Approval Policy Mapping', path: '/travel/policy-mapping', icon: ShieldCheck },
        { label: 'Travel Budgets', path: '/travel/budget', icon: Wallet },
        { label: 'Company Credit Cards', path: '/travel/credit-cards', icon: CreditCard },
        { label: 'Vendor Management', path: '/travel/vendors', icon: Building2 },
        { label: 'Travel Reports', path: '/travel/reports', icon: FileBarChart2 },
        { label: 'Settings & Calendar', path: '/travel/settings', icon: CalendarDays },
      ] },
    { label: 'Manager', icon: UsersRound, path: '/manager',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'department_manager', 'is_reporting_manager'],
      submenu: [
        { label: 'Team Dashboard', path: '/manager/dashboard', icon: LayoutDashboard },
        { label: 'Approvals', path: '/manager/approvals', icon: CheckSquare },
        { label: 'Approval Report', path: '/manager/approval-report', icon: FileBarChart2 },
        { label: 'My Line-Item Inbox', path: '/line-item-approval/inbox', icon: ClipboardCheck },
      ] },
    { label: 'HR Workbench', icon: Inbox, path: '/hr-workbench',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'HR Bench (Input Control)', icon: ListChecks, path: '/hr-bench',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'finance_reviewer'] },
    { label: 'HR Bench Reports', icon: ListChecks, path: '/hr-bench/reports',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Document Templates', icon: Layers, path: '/hr/document-templates',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin'] },
    { label: 'Official Letters', icon: FileCheck, path: '/hr/appointment-orders',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin'] },
    { label: 'Increment Workbench', icon: TrendingUp, path: '/hr/increment-workbench',
      roles: ['tenant_admin', 'hr_admin', 'hr_manager', 'payroll_admin', 'department_manager'] },
    { label: 'Advance Settlements', icon: Wallet, path: '/advance-settlements',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Analytics', icon: BarChart3, path: '/analytics',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'] },
    { label: 'Reports', icon: FileBarChart2, path: '/reports',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Reports Center', path: '/reports', icon: FileBarChart2 },
        { label: 'TDS Validation', path: '/reports/tds-validation', icon: FileBarChart2 },
        { label: 'Approval Reports', path: '/reports/approvals', icon: FileBarChart2 },
      ] },
    { label: 'Finance', icon: Landmark, path: '/finance',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Finance Overview', path: '/finance', icon: Landmark },
        { label: 'Local Conveyance Payment', path: '/finance/local-travel-review', icon: MapPin },
        { label: 'Travel Payment', path: '/finance/travel-payments', icon: Plane },
        { label: 'Travel & Conveyance Ledger', path: '/finance/travel-conveyance-ledger', icon: BookOpen },
      ] },
    { label: 'Business Controls', icon: Shield, path: '/business-controls',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Employees', icon: UsersRound, path: '/employees',
      roles: ['tenant_admin', 'hr_admin'],
      submenu: [
        { label: 'Employees List', path: '/employees', icon: UsersRound },
        { label: 'ESS Approvals', path: '/hr/ess-approvals', icon: CheckSquare },
        { label: 'KYC Cycles', path: '/payroll/kyc-cycles', icon: ShieldCheck },
        { label: 'KYC Corrections', path: '/payroll/kyc-corrections', icon: CheckSquare },
      ] },
    { label: 'Leave', icon: TreePalm, path: '/leave',
      roles: ['tenant_admin', 'hr_admin', 'department_manager', 'payroll_admin'],
      submenu: [
        { label: 'Leave Management', path: '/leave?tab=applications', icon: TreePalm },
        { label: 'Permissions', path: '/leave?tab=permissions', icon: Clock },
        { label: 'Permission Calendar', path: '/leave?tab=permission-calendar', icon: Calendar },
        { label: 'Leave Types', path: '/leave?tab=leave-types', icon: FileText },
        { label: 'Leave Balances', path: '/leave?tab=balances', icon: Wallet },
        { label: 'Leave Policy', path: '/leave?tab=policy', icon: BookOpen },
        { label: 'Policy Master', path: '/leave?tab=policy-master', icon: ShieldCheck },
      ] },
    { label: 'Attendance', icon: Clock, path: '/attendance',
      roles: ['tenant_admin', 'hr_admin', 'attendance_admin', 'department_manager', 'payroll_admin', 'finance_reviewer'],
      submenu: [
        { label: 'Dashboard', path: '/attendance', icon: LayoutDashboard },
        { label: '── Operations', path: '#sep1', icon: Clock, separator: true },
        { label: 'Upload / Import', path: '/attendance/upload', icon: FileText },
        { label: 'Upload History', path: '/attendance/history', icon: BookOpen },
        { label: 'Internal Attendance', path: '/attendance/internal', icon: ClipboardCheck },
        { label: 'Calculation', path: '/attendance/calculation', icon: Calculator },
        { label: 'Reports', path: '/attendance/reports', icon: FileBarChart2 },
        { label: '── Daily View', path: '#sep2', icon: Clock, separator: true },
        { label: 'Daily Register', path: '/attendance/daily', icon: CalendarDays },
        { label: 'Monthly Summary', path: '/attendance/monthly', icon: Calendar },
        { label: 'Punch Logs', path: '/attendance/punches', icon: Clock },
        { label: '── Configuration', path: '#sep3', icon: Clock, separator: true },
        { label: 'Biometric Devices', path: '/attendance/biometric', icon: Shield },
        { label: 'Device Mapping', path: '/attendance/biometric/mapping', icon: UserCheck },
        { label: 'eTimeTrackLite Import', path: '/attendance/biometric/import', icon: FileText },
        { label: 'Face Attendance', path: '/attendance/face-logs', icon: UserCheck },
        { label: 'Face Punch (Self)', path: '/attendance/face-punch', icon: ScanFace },
        { label: 'Geo-fence Setup', path: '/attendance/geofence', icon: Database },
        { label: 'Shift Roster', path: '/attendance/roster', icon: ArrowLeftRight },
        { label: '── Approvals', path: '#sep4', icon: Clock, separator: true },
        { label: 'Regularization', path: '/attendance/regularization', icon: FileSearch },
        { label: 'OT Approval', path: '/attendance/ot-approval', icon: Calculator },
        { label: 'Exception Register', path: '/attendance/exception-register', icon: AlertTriangle },
      ] },
    { label: 'Payroll', icon: Wallet, path: '/payroll',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: '── Setup & Masters', path: '#sep-pay1', icon: Wallet, separator: true },
        { label: 'Payroll Setup', path: '/payroll/setup', icon: CalendarDays },
        { label: 'Wage Master', path: '/payroll/wage-master', icon: Coins },
        { label: 'Structure Overview', path: '/payroll/structures', icon: Receipt },
        { label: 'CTC Planner', path: '/payroll/ctc-planner', icon: Calculator },
        { label: 'Employee CTC Upload', path: '/payroll/employee-ctc-upload', icon: FileText },
        { label: 'Legacy / Historical Import', path: '/payroll/legacy-imports', icon: History },
        { label: '── Inputs (per cycle)', path: '#sep-pay2', icon: Wallet, separator: true },
        { label: 'Monthly Inputs', path: '/monthly-inputs', icon: ClipboardList },
        { label: 'Arrears', path: '/payroll/arrears', icon: FileText },
        { label: 'Advances', path: '/payroll/loans', icon: CreditCard },
        { label: 'Advance Policy', path: '/payroll/loan-policy', icon: ShieldCheck },
        { label: 'Reimbursements Tracker', path: '/payroll/reimbursements-tracker', icon: Receipt },
        { label: 'Reimbursement Policy', path: '/payroll/reimbursement-policy', icon: ShieldCheck },
        { label: 'Expense Sheets Review', path: '/payroll/expense-claims-admin', icon: Receipt },
        { label: 'Approval Workflows', path: '/payroll/approval-workflows', icon: ShieldCheck },
        { label: 'Salary Revision', path: '/payroll/salary-revision', icon: TrendingUp },
        { label: '── Execution', path: '#sep-pay3', icon: Wallet, separator: true },
        { label: 'Consolidated Approval', path: '/consolidated-approval', icon: ClipboardCheck },
        { label: 'Approval Dashboard', path: '/line-item-approval/dashboard', icon: ClipboardCheck },
        { label: 'My Approval Inbox', path: '/line-item-approval/inbox', icon: ClipboardCheck },
        { label: 'Approval Rules', path: '/line-item-approval/workflows', icon: Wrench },
        { label: 'Monthly Run', path: '/payroll/run', icon: Banknote },
        { label: 'Exceptions', path: '/payroll/exceptions', icon: AlertTriangle },
        { label: 'Approval', path: '/payroll/approval', icon: ClipboardCheck },
        { label: '── Output', path: '#sep-pay4', icon: Wallet, separator: true },
        { label: 'Payslips', path: '/payroll/payslips', icon: FileBarChart },
        { label: 'Tax Sheets', path: '/payroll/tax-sheets', icon: FileText },
        { label: 'F&F Settlement', path: '/payroll/fnf', icon: ArrowLeftRight },
      ] },
    { label: 'Statutory', icon: ShieldCheck, path: '/statutory',
      roles: ['tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'],
      submenu: [
        { label: '── Setup', path: '#sep-stat1', icon: ShieldCheck, separator: true },
        { label: 'Setup Master', path: '/statutory/setup', icon: Wrench },
        { label: 'State-wise PT & LWF', path: '/statutory/state-rates', icon: MapPin },
        { label: 'Statutory Masters', path: '/statutory/masters', icon: Wrench },
        { label: 'Employee Eligibility', path: '/statutory/eligibility', icon: UserCheck },
        { label: '── Declarations', path: '#sep-stat2', icon: ShieldCheck, separator: true },
        { label: 'Declarations & Proofs', path: '/statutory/declarations', icon: FileSearch },
        { label: 'TDS Projection', path: '/statutory/tds-projection', icon: TrendingUp },
        { label: '── Working', path: '#sep-stat3', icon: ShieldCheck, separator: true },
        { label: 'PF/ESI/PT/LWF Working', path: '/statutory/working', icon: Calculator },
        { label: 'Challan Preparation', path: '/statutory/challans', icon: FileSignature },
        { label: '── Filings & Registers', path: '#sep-stat4', icon: ShieldCheck, separator: true },
        { label: 'Monthly Register', path: '/statutory/register', icon: BookOpen },
        { label: 'Annual Tax Worksheet', path: '/statutory/tax-worksheet', icon: FileBarChart },
        { label: '── Compliance', path: '#sep-stat5', icon: ShieldCheck, separator: true },
        { label: 'Compliance Dashboard', path: '/statutory/compliance', icon: Gauge },
      ] },
    { label: 'Master Setup', icon: Sparkles, path: '/masters/setup',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin'] },
    { label: 'Local Conveyance Policy', icon: MapPin, path: '/masters/local-conveyance-policy',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Asset & Doc Types', icon: Package, path: '/masters/asset-document-types',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Tax Slab Master', icon: Layers, path: '/masters/tax-slabs',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Help & Guide', icon: BookOpen, path: '/admin/processing-guide',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer'] },
    { label: 'Companies', icon: Building2, path: '/companies',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Company Broadcasts', icon: Megaphone, path: '/company-broadcasts',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin'] },
    { label: 'Organization', icon: Network, path: '/organization',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Users', icon: Users, path: '/users',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Roles', icon: Shield, path: '/roles',
      roles: ['tenant_admin'] },
    { label: 'Audit Logs', icon: FileText, path: '/audit-logs',
      roles: ['tenant_admin', 'hr_admin', 'finance_reviewer'] },
    { label: 'Bulk Data Reset', icon: Trash2, path: '/admin/data-reset',
      roles: ['tenant_admin', 'hr_admin'] },
    { label: 'Notifications', icon: Bell, path: '/notifications',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
    { label: 'Action Inbox', icon: CheckSquare, path: '/action-inbox',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'finance_reviewer', 'department_manager'] },
    { label: 'Profile', icon: User, path: '/profile',
      roles: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'] },
  ];

  const menuItems = isSuperAdmin
    ? platformMenu.map((m) => ({ ...m, roles: ['super_admin'] }))
    : tenantMenu;

  const moduleKeyByLabel = {
    Payroll: 'payroll', Attendance: 'attendance', Leave: 'leave',
    Statutory: 'statutory', Employees: 'employees', Reports: 'reports',
    Finance: 'finance', Analytics: 'analytics', 'Outstation Conveyance': 'travel_desk', 'Travel Desk': 'travel_desk',
  };
  const enabledModules = Array.isArray(user?.enabled_modules) ? user.enabled_modules : null;
  const isModuleVisible = (label) => {
    if (isSuperAdmin) return true;
    const key = moduleKeyByLabel[label];
    if (!key) return true;
    if (!enabledModules) return true;
    return enabledModules.includes(key);
  };

  const hasPerms = user && Array.isArray(user.permissions) && user.permissions.length > 0;
  const isCustomRole = (user?.role || '').startsWith('custom:');
  const passesLegacyRoleCheck = (item) => {
    if (isReportingManager && (item.roles || []).includes('is_reporting_manager')) return true;
    if (isCustomRole) return true;
    const userRole = user?.role;
    const userRoles = Array.isArray(user?.roles) ? user.roles : [userRole];
    const isCompanyAdmin = userRole === 'COMPANY_ADMIN' || userRole === 'company_admin' || userRoles.includes('COMPANY_ADMIN') || userRoles.includes('company_admin') || userRole === 'tenant_admin' || userRoles.includes('tenant_admin');

    if (!item.roles || item.roles.length === 0) return true;
    if (isCompanyAdmin && item.roles.includes('tenant_admin')) return true;

    return item.roles.some((r) => userRoles.includes(r) || (isCompanyAdmin && r === 'tenant_admin'));
  };

  const viewGate = (item) => {
    if (isSuperAdmin) return true;
    if (!isReportingManager) return true;
    if (activeView === 'manager') {
      const managerModeAllowed = new Set(['Dashboard', 'Manager', 'HR Workbench']);
      return managerModeAllowed.has(item.label);
    }
    return item.label !== 'Manager';
  };

  const filteredMenu = menuItems
    .filter((item) => passesLegacyRoleCheck(item) && isModuleVisible(item.label) && viewGate(item))
    .map((item) => {
      if (user?.role === 'super_admin') return item;
      const enforceMatrix = isCustomRole || hasPerms;
      if (!enforceMatrix) return item;

      if (!item.submenu) {
        return canViewPath(user, item.path) ? item : null;
      }

      const realVisible = item.submenu.filter(
        (sub) => !sub.separator && canViewPath(user, sub.path)
      );
      if (realVisible.length === 0) return null;

      const visibleSubs = item.submenu.filter(
        (sub) => sub.separator || canViewPath(user, sub.path)
      );
      const cleaned = [];
      let pendingSep = null;
      for (const sub of visibleSubs) {
        if (sub.separator) {
          pendingSep = sub;
        } else {
          if (pendingSep) {
            cleaned.push(pendingSep);
            pendingSep = null;
          }
          cleaned.push(sub);
        }
      }
      return { ...item, submenu: cleaned };
    })
    .filter(Boolean);

  const menuRef = useRef();
  menuRef.current = filteredMenu;
  useEffect(() => {
    const fullPath = location.pathname + (location.search || '');
    const match = (menuRef.current || []).find(
      (item) =>
        item.submenu &&
        (item.submenu.some(
          (s) =>
            s.path === fullPath ||
            s.path === location.pathname ||
            (s.path.includes('?') && fullPath.startsWith(s.path.split('?')[0]))
        ) ||
          (item.path === '/leave' && location.pathname.startsWith('/leave')) ||
          (item.path === '/travel' && location.pathname.startsWith('/travel')))
    );
    if (match) {
      setExpandedMenu(match.label);
      sessionStorage.setItem('sidebar-expanded', match.label);
    }
  }, [location.pathname, location.search]);

  const isActive = (path) => {
    const fullPath = location.pathname + (location.search || '');
    if (fullPath === path) return true;
    if (location.pathname === path && !location.search) return true;
    if (path.includes('?') && path.startsWith(location.pathname)) {
      const pTab = new URLSearchParams(path.split('?')[1]).get('tab');
      const cTab = new URLSearchParams(location.search).get('tab') || 'applications';
      if (pTab === cTab) return true;
    }
    if (
      path === '/travel/requests' &&
      location.pathname.startsWith('/travel/request/') &&
      location.pathname !== '/travel/request/new'
    ) {
      return true;
    }
    return false;
  };

  const isParentActive = (item) => {
    if (!item.submenu) return false;
    if (item.path === '/leave' && location.pathname.startsWith('/leave')) return true;
    if (item.path === '/travel' && location.pathname.startsWith('/travel')) return true;
    return item.submenu.some((s) => location.pathname === s.path.split('?')[0]);
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Light Professional Enterprise Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen bg-white text-slate-800 border-r border-slate-200/90 shadow-sm flex flex-col z-40 transition-all duration-300 ease-in-out overflow-hidden
          ${
            isOpen
              ? 'translate-x-0 w-[280px]'
              : '-translate-x-full w-[280px] md:translate-x-0 md:w-20 lg:w-[280px]'
          }`}
      >
        {/* Sidebar Header */}
        <div className="p-4 md:p-3 lg:p-5 border-b border-slate-100 bg-slate-50/40 flex items-center justify-between shrink-0 w-full">
          <div className="flex items-center gap-3 md:gap-0 lg:gap-3 w-full md:justify-center lg:justify-start">
            <img
              src={
                branding.logo_url ||
                'https://hecs.in/storage/logo/hecs-logo-1.png'
              }
              alt="Logo"
              className="w-9 h-9 object-contain shrink-0"
            />
            <div className={`md:hidden lg:block ${isOpen ? 'block' : ''}`}>
              <h1 className="text-base font-extrabold tracking-tight text-slate-900 whitespace-nowrap">
                {branding.app_name || 'PayrollPro'}
              </h1>
              <p className="text-[11px] font-semibold text-[#0052FF]">
                Enterprise Suite
              </p>
            </div>
          </div>
          {isOpen && (
            <button
              onClick={onClose}
              className="md:hidden text-slate-400 hover:text-slate-600 p-1 rounded-md"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Scrollable Navigation */}
        <nav
          ref={navRef}
          className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 scrollbar-thin w-full"
        >
          {filteredMenu.map((item) => {
            if (item.submenu) {
              const isExpanded = expandedMenu === item.label;
              const parentActive = isParentActive(item);
              return (
                <SidebarGroup
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  parentActive={parentActive}
                  isExpanded={isExpanded}
                  onExpandClick={() => handleExpand(item.label)}
                  isOpen={isOpen}
                >
                  {item.submenu.map((sub) => {
                    if (sub.separator) {
                      return (
                        <SidebarSection
                          key={sub.path}
                          label={sub.label}
                          isOpen={isOpen}
                        />
                      );
                    }
                    return (
                      <SidebarItem
                        key={sub.path}
                        to={sub.path}
                        label={sub.label}
                        icon={sub.icon}
                        active={isActive(sub.path)}
                        onClick={onClose}
                        isChild={true}
                        isOpen={isOpen}
                      />
                    );
                  })}
                </SidebarGroup>
              );
            }
            return (
              <SidebarItem
                key={item.path}
                to={item.path}
                label={item.label}
                icon={item.icon}
                active={isActive(item.path)}
                onClick={onClose}
                isChild={false}
                isOpen={isOpen}
                unreadBadge={item.label === 'Notifications' ? unreadCount : 0}
              />
            );
          })}
        </nav>

        {/* Sidebar Footer User Profile Card */}
        <SidebarFooter user={user} isOpen={isOpen} />
      </div>
    </>
  );
};

export default Sidebar;
